import React from "react";
import {
  FaIdBadge,
  FaTruckMoving,
  FaCalendarAlt,
  FaUsers,
  FaTimesCircle,
  FaInfoCircle,
  FaFileAlt,
} from "react-icons/fa";
import { BsFillClockFill } from "react-icons/bs";
import dayjs from "dayjs";
import "../../styles/LeftPanelPosition.css";

const LeftPanelPreview = ({
  data,
  recruiterOptions,
  categories,
  orgUnits,
  positionTypes,
}) => {
  if (!data) return null;

  const {
    p_position_title,
    p_position_number,
    p_position_type,
    p_posting = {},
    p_visible_info = {},
    p_landing_page_sell = {},
    p_reqs = {},
    p_contact_info = {},
    p_report_to = {},
    p_pay_rates = {},
    p_driver_reqs = {},
    p_orientation = {},
    p_division_policies = {},
    p_landing_page_desc,
    p_internal_desc,
    p_comments,
    p_created_at,
    p_status,
  } = data;

  const resolveUserName = (userId) => {
    const user = recruiterOptions?.find((u) => u.value === userId);
    return user ? user.label : "-";
  };

  const resolveNameById = (list, id, idKey, nameKey) => {
    const found = list?.find(
      (item) => item[idKey]?.toString() === id?.toString()
    );

    if (!found) return "-";

    // If the field is itself an object, return TranslatedText or Name
    const val = found[nameKey];
    if (typeof val === "object" && val !== null) {
      return val.TranslatedText || val.Name || JSON.stringify(val);
    }

    return val || "-";
  };

  return (
    <>
      {/* <div className="d-flex justify-content-end mb-3">
                <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
            </div> */}

      {/* Position Summary Section */}
      <div className="section mb-4">
        <div className="section-title">Position Description</div>
        <div className="section-content">
          <h5 className="fw-bold mb-3">
            {p_position_title || "Position Title"}
          </h5>
          <ul className="list-unstyled small text-muted mb-4">
            <li>
              <FaIdBadge className="me-2" /> <strong>Position #:</strong>{" "}
              {p_position_number || "-"}
            </li>
            <li>
              <FaTimesCircle className="me-2" />{" "}
              <strong>Dangerous Goods:</strong>{" "}
              {p_landing_page_sell?.dangerous_goods === "true" ? "Yes" : "No"}
            </li>
            <li>
              <BsFillClockFill className="me-2" /> <strong>Type:</strong>{" "}
              {p_position_type || "-"}
            </li>
            <li>
              <FaTimesCircle className="me-2" />{" "}
              <strong>Licence Class: </strong> Class{" "}
              {Array.isArray(p_landing_page_sell?.license_class)
                ? p_landing_page_sell.license_class.join(", ")
                : p_landing_page_sell?.license_class || "-"}
            </li>
            <li>
              <FaTruckMoving className="me-2" /> <strong>Route:</strong> Canada
              Only
            </li>
            <li>
              <FaUsers className="me-2" /> <strong>Openings:</strong>{" "}
              {p_posting?.p_num_openings || "-"}
            </li>
            <li>
              <FaCalendarAlt className="me-2" /> <strong>Closing:</strong>{" "}
              {p_posting?.p_closing_date
                ? dayjs(p_posting.p_closing_date).format("MM/DD/YYYY")
                : "-"}
            </li>
          </ul>

          {p_landing_page_desc && (
            <div
              className="job-description"
              dangerouslySetInnerHTML={{ __html: p_landing_page_desc }}
            />
          )}
        </div>
      </div>

      {/* Internal Section */}
      <div className="section mb-4">
        <div className="section-title">
          Position / Job Description
          <small className="section-hint">
            <FaInfoCircle className="me-1" />
            This description is for internal use only and does not get posted to
            external job boards or display on the landing page.
          </small>
        </div>
        <div className="section-content">
          {p_internal_desc && (
            <div
              className="int-description"
              dangerouslySetInnerHTML={{ __html: p_internal_desc }}
            />
          )}
        </div>
        <div className="section-subtitle mt-3">
          Comments / Special Instructions
        </div>
        <div className="section-content">
          {(p_comments && (
            <div
              className="p_comments"
              dangerouslySetInnerHTML={{ __html: p_comments }}
            />
          )) ||
            "-"}
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaIdBadge className="me-2" /> Position Details
        </div>

        <div className="position-detail-layout">
          <table className="detail-table">
            <tbody>
              <tr>
                <td>Position Title</td>
                <td>{p_position_title || "-"}</td>
              </tr>
              <tr>
                <td>Category</td>
                <td>
                  {resolveNameById(
                    categories,
                    data.p_category,
                    "c_id",
                    "c_name"
                  )}
                </td>
              </tr>
              <tr>
                <td>Sub Category</td>
                <td>{data.p_sub_category || "-"}</td>
              </tr>
              <tr>
                <td>Position #</td>
                <td>{p_position_number || "-"}</td>
              </tr>
              <tr>
                <td>Org Unit</td>
                <td>
                  {resolveNameById(
                    orgUnits,
                    data.p_org_unit,
                    "ou_id",
                    "ou_name"
                  )}
                </td>
              </tr>
              <tr>
                <td>Position Type</td>
                <td>
                  {resolveNameById(
                    positionTypes,
                    data.p_position_type,
                    "pt_id",
                    "pt_name"
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="shift-table">
            <thead>
              <tr>
                <th colSpan={4}>Shift Availability Requirements</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Monday", "Tuesday", "Wednesday", "Thursday"],
                ["Friday", "Saturday", "Sunday", "Spare Board"],
                ["Day", "Night", "Evening", "Operates On Holidays"],
              ].map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((shift) => (
                    <td key={shift}>
                      <input
                        type="checkbox"
                        disabled
                        checked={
                          Array.isArray(data.p_shifts) &&
                          data.p_shifts.includes(shift)
                        }
                      />{" "}
                      {shift}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Posting Details
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Posted Status</td>
                <td>{p_status === 1 ? "Open" : "Closed" || "-"}</td>
              </tr>
              <tr>
                <td>Date Posted</td>
                <td>
                  {p_posting?.p_date_posted
                    ? dayjs(p_posting.p_date_posted).format("MM/DD/YYYY")
                    : "-"}
                </td>
              </tr>
              <tr>
                <td>Closing Date</td>
                <td>
                  {p_posting?.p_closing_date
                    ? dayjs(p_posting.p_closing_date).format("MM/DD/YYYY")
                    : "-"}
                </td>
              </tr>
              <tr>
                <td>Openings</td>
                <td>{p_posting?.p_num_openings || "-"}</td>
              </tr>
              <tr>
                <td>Created By</td>
                <td>{resolveUserName(p_posting?.p_created_by)}</td>
              </tr>
              <tr>
                <td>Created Date</td>
                <td>
                  {p_created_at
                    ? dayjs(p_created_at).format("MM/DD/YYYY")
                    : "-"}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Send Posting To</td>
                <td>{resolveUserName(p_posting?.p_send_posting_to)}</td>
              </tr>
              <tr>
                <td>Send to Group(s)</td>
                <td>
                  {Array.isArray(p_posting?.p_send_group)
                    ? p_posting.p_send_group.join(", ")
                    : "-"}
                </td>
              </tr>
              <tr>
                <td>Reason For Replacement</td>
                <td>{p_posting?.p_reason_for_replacement || "-"}</td>
              </tr>
              <tr>
                <td>Reason For New Hire</td>
                <td>{p_posting?.p_reason_for_new_hire || "-"}</td>
              </tr>
              <tr>
                <td>Send notifications to</td>
                <td>{p_posting?.p_notify_to || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Visible Information
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Dangerous Goods / Hazmat</td>
                <td>
                  {p_visible_info?.dangerous_goods === "true"
                    ? "Shown"
                    : "Hidden" || "-"}
                </td>
              </tr>
              <tr>
                <td>Licence Classes</td>
                <td>
                  {p_visible_info?.license_classes === "true"
                    ? "Shown"
                    : "Hidden" || "-"}
                </td>
              </tr>
              <tr>
                <td>Openings</td>
                <td>
                  {p_visible_info?.openings === "true"
                    ? "Shown"
                    : "Hidden" || "-"}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Work Type</td>
                <td>
                  {p_visible_info?.work_type === "true"
                    ? "Shown"
                    : "Hidden" || "-"}
                </td>
              </tr>
              <tr>
                <td>Route</td>
                <td>
                  {p_visible_info?.route === "true" ? "Shown" : "Hidden" || "-"}
                </td>
              </tr>
              <tr>
                <td>Closing Date</td>
                <td>
                  {p_visible_info?.closing_date === "true"
                    ? "Shown"
                    : "Hidden" || "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Landing Page Sell
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Show on landing page(s)</td>
                <td>{p_landing_page_sell?.show_on_landing_pages || "-"}</td>
              </tr>
              <tr>
                <td>Licence Class</td>
                <td>{p_landing_page_sell?.license_class || "-"}</td>
              </tr>
              <tr>
                <td>Canada-US (DOT)</td>
                <td>{p_landing_page_sell?.canada_us_dot || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Single or Team Driver</td>
                <td>{p_landing_page_sell?.driver_type || "-"}</td>
              </tr>
              <tr>
                <td>Dangerous Goods / Hazmat</td>
                <td>{p_landing_page_sell?.dangerous_goods || "-"}</td>
              </tr>
              <tr>
                <td>This position is</td>
                <td>{p_landing_page_sell?.position_type || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Requirements
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Start Date</td>
                <td>{p_reqs?.p_start_date || "-"}</td>
              </tr>
              {/* <tr><td>Hand Bombing Required</td><td>{p_reqs?.license_class}</td></tr> */}
              <tr>
                <td>Home Time Policy</td>
                <td>{p_reqs?.p_home_time_policy || "-"}</td>
              </tr>
              <tr>
                <td>Areas Of Travel</td>
                <td>
                  {Array.isArray(p_reqs?.p_travel_areas)
                    ? p_reqs.p_travel_areas.join(", ")
                    : p_reqs?.p_travel_areas || "-"}
                </td>
              </tr>
              <tr>
                <td>Freight Types</td>
                <td>
                  {Array.isArray(p_reqs?.p_freight_types)
                    ? p_reqs.p_freight_types.join(", ")
                    : p_reqs?.p_freight_types || "-"}
                </td>
              </tr>
              <tr>
                <td>Length Of Run</td>
                <td>{p_reqs?.p_length_of_run || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Dedicated Trucks</td>
                <td>
                  {p_reqs?.p_dedicated_truck === "true" ? "Yes" : "No" || "-"}
                </td>
              </tr>
              <tr>
                <td>Dedicated Run</td>
                <td>
                  {p_reqs?.p_dedicated_run === "true" ? "Yes" : "No" || "-"}
                </td>
              </tr>
              <tr>
                <td>Open Board</td>
                <td>{p_reqs?.p_open_board === "true" ? "Yes" : "No" || "-"}</td>
              </tr>
              <tr>
                <td>Trip Envelopes Required</td>
                <td>
                  {p_reqs?.p_trip_envelopes === "true" ? "Yes" : "No" || "-"}
                </td>
              </tr>
              <tr>
                <td>Trip Sheet Drop Locations</td>
                <td>{p_reqs?.p_trip_drop || "-"}</td>
              </tr>
              <tr>
                <td>Parking Requirements</td>
                <td>{p_reqs?.p_parking_req || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Contact Information
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Name</td>
                <td>{p_contact_info?.name || "-"}</td>
              </tr>
              <tr>
                <td>Title</td>
                <td>{p_contact_info?.title || "-"}</td>
              </tr>
              <tr>
                <td>Toll-Free Phone</td>
                <td>{p_contact_info?.toll_free || "-"}</td>
              </tr>
              <tr>
                <td>Local Phones</td>
                <td>{p_contact_info?.local || "-"}</td>
              </tr>
              <tr>
                <td>Email</td>
                <td>{p_contact_info?.email || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Address 1</td>
                <td>{p_contact_info?.address || "-"}</td>
              </tr>
              <tr>
                <td>Address 2</td>
                <td>{p_contact_info?.address2 || "-"}</td>
              </tr>
              <tr>
                <td>Postal Code</td>
                <td>{p_contact_info?.postal || "-"}</td>
              </tr>
              <tr>
                <td>City</td>
                <td>{p_contact_info?.city || "-"}</td>
              </tr>
              <tr>
                <td>Province</td>
                <td>{p_contact_info?.province || "-"}</td>
              </tr>
              <tr>
                <td>Country</td>
                <td>{p_contact_info?.country || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Report To Information
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Name</td>
                <td>{p_report_to?.name || "-"}</td>
              </tr>
              <tr>
                <td>Title</td>
                <td>{p_report_to?.title || "-"}</td>
              </tr>
              <tr>
                <td>Toll-Free Phone</td>
                <td>{p_report_to?.toll_free || "-"}</td>
              </tr>
              <tr>
                <td>Local Phones</td>
                <td>{p_report_to?.local || "-"}</td>
              </tr>
              <tr>
                <td>Email</td>
                <td>{p_report_to?.email || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Address 1</td>
                <td>{p_report_to?.address || "-"}</td>
              </tr>
              <tr>
                <td>Address 2</td>
                <td>{p_report_to?.address2 || "-"}</td>
              </tr>
              <tr>
                <td>Postal Code</td>
                <td>{p_report_to?.postal || "-"}</td>
              </tr>
              <tr>
                <td>City</td>
                <td>{p_report_to?.city || "-"}</td>
              </tr>
              <tr>
                <td>Province</td>
                <td>{p_report_to?.province || "-"}</td>
              </tr>
              <tr>
                <td>Country</td>
                <td>{p_report_to?.country || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Pay Rates (display only)
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Mileage Rate</td>
                <td>{p_pay_rates?.mileage_rate || "-"}</td>
              </tr>
              <tr>
                <td>Waiting Time Rate</td>
                <td>{p_pay_rates?.waiting_time_rate || "-"}</td>
              </tr>
              <tr>
                <td>Layover Rate</td>
                <td>{p_pay_rates?.layover_rate || "-"}</td>
              </tr>
              <tr>
                <td>Breakdown Rate</td>
                <td>{p_pay_rates?.breakdown_rate || "-"}</td>
              </tr>
              <tr>
                <td>Tarp Rate</td>
                <td>{p_pay_rates?.tarp_rate || "-"}</td>
              </tr>
              <tr>
                <td>Waiting Policy</td>
                <td>{p_pay_rates?.waiting_policy || "-"}</td>
              </tr>
              <tr>
                <td>Layover Policy</td>
                <td>{p_pay_rates?.layover_policy || "-"}</td>
              </tr>
              <tr>
                <td>Avg Miles Per Trip</td>
                <td>{p_pay_rates?.avg_miles_per_trip || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Pick Rate</td>
                <td>{p_pay_rates?.pick_rate || "-"}</td>
              </tr>
              <tr>
                <td>Drop Rate</td>
                <td>{p_pay_rates?.drop_rate || "-"}</td>
              </tr>
              <tr>
                <td>Load Rate</td>
                <td>{p_pay_rates?.load_rate || "-"}</td>
              </tr>
              <tr>
                <td>Unload Rate</td>
                <td>{p_pay_rates?.unload_rate || "-"}</td>
              </tr>
              <tr>
                <td>Border Crossing Rate</td>
                <td>{p_pay_rates?.border_crossing_rate || "-"}</td>
              </tr>
              <tr>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>Hourly Rate</td>
                <td>{p_pay_rates?.hourly || "-"}</td>
              </tr>
              <tr>
                <td>Avg. Gross Earnings {p_pay_rates?.earnings_freq || "-"}</td>
                <td>{p_pay_rates?.gross_earnings || "-"}</td>
              </tr>
              <tr>
                <td>Avg Hours Per Day</td>
                <td>{p_pay_rates?.hours_per_day || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Driver Requirements
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Years Of Experience</td>
                <td>{p_driver_reqs?.years_exp || "-"}</td>
              </tr>
              <tr>
                <td>FAST Card Required?</td>
                <td>{p_driver_reqs?.fast_card || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Orientation
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Orientation</td>
                <td>{p_orientation?.orientation || "-"}</td>
              </tr>
              <tr>
                <td>Length</td>
                <td>{p_orientation?.length || "-"}</td>
              </tr>
              <tr>
                <td>Paid</td>
                <td>{p_orientation?.paid || "-"}</td>
              </tr>
              {/* <tr><td>Pay Rate</td><td>{p_orientation?.title || '-'}</td></tr> */}
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Hotel Accommodation Provided?</td>
                <td>{p_orientation?.hotel || "-"}</td>
              </tr>
              <tr>
                <td>Location</td>
                <td>{p_orientation?.province || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="section mb-4">
        <div className="section-title">
          <FaFileAlt className="me-2" /> Division Policies
        </div>

        <div className="posting-detail-layout">
          <table className="posting-table">
            <tbody>
              <tr>
                <td>Mentoring Provided</td>
                <td>{p_division_policies?.mentoring || "-"}</td>
              </tr>
              <tr>
                <td>Riders Policy</td>
                <td>{p_division_policies?.riders || "-"}</td>
              </tr>
            </tbody>
          </table>

          <table className="posting-table">
            <tbody>
              <tr>
                <td>Pets Policy</td>
                <td>{p_division_policies?.pets || "-"}</td>
              </tr>
              <tr>
                <td>Contractors Policy</td>
                <td>{p_division_policies?.contractors || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="small text-muted">
        We are an equal opportunity employer and value diversity.
      </p>
    </>
  );
};

export default LeftPanelPreview;
